import {
  CAMERA_SELECTION_SCOPE,
  EVENT_LABELLING_DELETE_SCOPE,
  EVENT_LABELLING_SCOPE,
  MANUAL_REVIEW_SCOPE,
  NAVIGATION_SCOPE,
  LABELLING_KEYBOARD_SCOPE,
} from "components/manualReview/hotkeyScopes";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import {
  useVideoControlActions,
  useVideoControlState,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { useIsHotkeyScopeEnabled } from "components/scrapEyeLayout/hotkeys/useIsHotkeyScopeEnabled";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys, useHotkeysContext } from "react-hotkeys-hook";
import { EventType } from "src/store/api/generatedApi";
import { ScrapEyesEvent } from "../types";
import { useAreaHotkeyMode } from "./useAreaHotkeyMode";
import {
  useHotkeyCodeToAreaMapping,
  useNumberedBinAreas,
} from "./useAreaHotkeys";
import { BinEntryState, reduceBinEntryKey } from "./useBinNumberEntry";
import { useDisableHotkeyScopes } from "./useDisableHotkeyScopes";
import { PendingAreaSelection } from "./usePendingAreaSelection";
import { useRecordingRelease } from "./useRecordingRelease";
import { useShiftMaterialHotkeys } from "./useShiftMaterialHotkeys";

const OTHER_SCOPES = [
  EVENT_LABELLING_SCOPE,
  EVENT_LABELLING_DELETE_SCOPE,
  MANUAL_REVIEW_SCOPE,
  CAMERA_SELECTION_SCOPE,
  NAVIGATION_SCOPE,
] as const;

const BIN_ENTRY_TRIGGER_CODE = "KeyB";
const INACTIVE_BIN_ENTRY: BinEntryState = { active: false, buffer: "" };

const isBinNumberKey = (code: string): boolean =>
  /^(?:Digit|Numpad)\d$/.test(code) || code === "Backspace";

const isEnterKey = (code: string): boolean =>
  code === "Enter" || code === "NumpadEnter";

export type AltAreaBehavior = "record" | "relabel";

export type LabellingKeyboardCallbacks = Readonly<{
  handleStartRecording: (eventType: EventType, area: string | null) => void;
  handleStopRecording: () => void;
  handleDeleteEvent: (id: string) => void;
  handleSetMaterial: (material: string | null) => void;
  handleSetArea: (area: string | null) => void;
  handleReverseEventType: () => void;
  handleAddFlag: () => void;
  handleSnapToCandidate: () => void;
  setPendingAreaSelection: (selection: PendingAreaSelection | null) => void;
  openAreaMaterialMappingModal: () => void;
  cycleAllAngles: () => void;
  resetAllToAuto: () => void;
}>;

type UseLabellingKeyboardArgs = Readonly<{
  scope?: string;
  events: readonly ScrapEyesEvent[];
  selectedEventId: string | null;
  recordingEventId: string | null;
  videoStart: dayjs.Dayjs;
  filteredMaterials: readonly { name: string }[];
  altAreaBehavior: AltAreaBehavior;
  callbacks: LabellingKeyboardCallbacks;
  location?: string;
}>;

type UseLabellingKeyboardResult = Readonly<{
  isAreaModeActive: boolean;
  isShiftHeld: boolean;
  binEntryActive: boolean;
  binBuffer: string;
  binEntryAvailable: boolean;
}>;

export const useLabellingKeyboard = ({
  scope = EVENT_LABELLING_SCOPE,
  events,
  selectedEventId,
  recordingEventId,
  videoStart,
  filteredMaterials,
  altAreaBehavior,
  callbacks,
  location,
}: UseLabellingKeyboardArgs): UseLabellingKeyboardResult => {
  const { getHotkey } = useHotkeyContext();
  const { currentTime } = useVideoControlState();
  const { pause, play } = useVideoControlActions();
  const { isScopeEnabled } = useIsHotkeyScopeEnabled();
  const hotkeyCodeToArea = useHotkeyCodeToAreaMapping(location);
  const { isAreaModeActive } = useAreaHotkeyMode(LABELLING_KEYBOARD_SCOPE);

  const binAreasByNumber = useNumberedBinAreas(location);
  const binEntryAvailable =
    binAreasByNumber.size > 0 && !(BIN_ENTRY_TRIGGER_CODE in hotkeyCodeToArea);
  const [binEntry, setBinEntry] = useState<BinEntryState>(INACTIVE_BIN_ENTRY);

  const { enableScope } = useHotkeysContext();
  useEffect(() => {
    enableScope(scope);
    enableScope(LABELLING_KEYBOARD_SCOPE);
  }, [enableScope, scope]);

  const scopesToDisable = useMemo(() => {
    // Exclude the delete scope so delete hotkey works in area mode
    return [
      ...OTHER_SCOPES.filter((s) => s !== EVENT_LABELLING_DELETE_SCOPE),
      scope,
    ];
  }, [scope]);
  useDisableHotkeyScopes(scopesToDisable, isAreaModeActive);

  // Refs to access latest values in window event listeners
  const isScopeEnabledRef = useRef(isScopeEnabled);
  useEffect(() => {
    isScopeEnabledRef.current = isScopeEnabled;
  }, [isScopeEnabled]);

  const eventsRef = useRef(events);
  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  const selectedEventIdRef = useRef(selectedEventId);
  useEffect(() => {
    selectedEventIdRef.current = selectedEventId;
  }, [selectedEventId]);

  const currentTimeRef = useRef(currentTime);
  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  const recordingEventIdRef = useRef(recordingEventId);
  useEffect(() => {
    recordingEventIdRef.current = recordingEventId;
  }, [recordingEventId]);

  const pauseRef = useRef(pause);
  useEffect(() => {
    pauseRef.current = pause;
  }, [pause]);

  const playRef = useRef(play);
  useEffect(() => {
    playRef.current = play;
  }, [play]);

  const getNextEventType = useCallback((): EventType => {
    const evts = eventsRef.current;
    if (evts.length === 0) return "lift";

    const currentTimestamp = videoStart.add(currentTimeRef.current, "second");
    const eventsBeforeCurrent = evts.filter((e) =>
      e.start.isBefore(currentTimestamp)
    );

    if (eventsBeforeCurrent.length === 0) return "lift";
    const lastEvent = eventsBeforeCurrent[eventsBeforeCurrent.length - 1];
    return lastEvent?.eventType === "lift" ? "drop" : "lift";
  }, [videoStart]);

  const handleStopRecordingWithModal = useCallback(
    (eventType: EventType) => {
      const eventId = recordingEventIdRef.current;
      callbacks.handleStopRecording();
      if (eventId) {
        callbacks.setPendingAreaSelection({
          eventId,
          eventType,
          openedOnKeyRelease: true,
        });
      }
    },
    [callbacks]
  );

  const recordingEventType = useMemo(
    () =>
      events.find((event) => event.id === recordingEventId)?.eventType ?? null,
    [events, recordingEventId]
  );

  useRecordingRelease({
    liftHotkey: getHotkey("liftEvent"),
    dropHotkey: getHotkey("dropEvent"),
    recordingEventId,
    recordingEventType,
    onRelease: handleStopRecordingWithModal,
    onInterrupt: callbacks.handleStopRecording,
  });

  const recordingHotkeyOptions = useMemo(
    () => ({
      keydown: true,
      preventDefault: true,
      scopes: [scope],
      ignoreEventWhen: (event: KeyboardEvent) => event.repeat,
    }),
    [scope]
  );

  useHotkeys(
    getHotkey("liftEvent"),
    () => {
      callbacks.handleStartRecording("lift", null);
    },
    recordingHotkeyOptions,
    [getHotkey, scope, callbacks.handleStartRecording]
  );

  useHotkeys(
    getHotkey("dropEvent"),
    () => {
      callbacks.handleStartRecording("drop", null);
    },
    recordingHotkeyOptions,
    [getHotkey, scope, callbacks.handleStartRecording]
  );

  useHotkeys(
    getHotkey("reverseLiftDrop"),
    callbacks.handleReverseEventType,
    { keydown: true, preventDefault: true, scopes: [scope] },
    [getHotkey, scope, callbacks.handleReverseEventType]
  );

  useHotkeys(
    getHotkey("addFlag"),
    callbacks.handleAddFlag,
    recordingHotkeyOptions,
    [getHotkey, scope, callbacks.handleAddFlag]
  );

  useHotkeys(
    getHotkey("snapToCandidate"),
    callbacks.handleSnapToCandidate,
    { keydown: true, preventDefault: true, scopes: [scope] },
    [getHotkey, scope, callbacks.handleSnapToCandidate]
  );

  useHotkeys(
    getHotkey("areaMaterialMapping"),
    callbacks.openAreaMaterialMappingModal,
    { keydown: true, preventDefault: true, scopes: [scope] },
    [getHotkey, scope, callbacks.openAreaMaterialMappingModal]
  );

  const ownsKeyboard = useCallback(
    () => isScopeEnabled(LABELLING_KEYBOARD_SCOPE),
    [isScopeEnabled]
  );
  const canSetMaterial = useCallback(
    () => !isAreaModeActive && isScopeEnabled(scope),
    [isAreaModeActive, isScopeEnabled, scope]
  );
  const { isShiftHeld } = useShiftMaterialHotkeys({
    filteredMaterials,
    ownsKeyboard,
    canSetMaterial,
    onSetMaterial: callbacks.handleSetMaterial,
  });

  const altHotkeyRecordingRef = useRef<{
    code: string;
    area: string | null;
    eventType: EventType;
  } | null>(null);

  // ref to get latest value without causing effect re-run
  const isAreaModeActiveRef = useRef(isAreaModeActive);
  useEffect(() => {
    isAreaModeActiveRef.current = isAreaModeActive;
  }, [isAreaModeActive]);

  const altAreaBehaviorRef = useRef(altAreaBehavior);
  useEffect(() => {
    altAreaBehaviorRef.current = altAreaBehavior;
  }, [altAreaBehavior]);

  const binEntryRef = useRef(binEntry);
  useEffect(() => {
    binEntryRef.current = binEntry;
  }, [binEntry]);
  const binAreasRef = useRef(binAreasByNumber);
  useEffect(() => {
    binAreasRef.current = binAreasByNumber;
  }, [binAreasByNumber]);
  const binEntryAvailableRef = useRef(binEntryAvailable);
  useEffect(() => {
    binEntryAvailableRef.current = binEntryAvailable;
  }, [binEntryAvailable]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Bail entirely when another surface owns the keyboard (e.g. the page while
      // the bring-in modal is open): nothing below should fire on a surface that
      // has handed off the keyboard.
      if (!isScopeEnabledRef.current(LABELLING_KEYBOARD_SCOPE)) {
        return;
      }

      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Check if area mode is active (handled by useAreaHotkeyMode)
      if (isAreaModeActiveRef.current) {
        const code = event.code;

        if (altAreaBehaviorRef.current === "relabel") {
          // Relabel mode never records: B+number (or an area key) just sets the
          // selected event's bin.
          const binOutcome = reduceBinEntryKey(
            binEntryRef.current,
            code,
            binAreasRef.current,
            binEntryAvailableRef.current,
            true
          );
          if (binOutcome.consumed) {
            event.preventDefault();
            if (binOutcome.nextState !== binEntryRef.current) {
              binEntryRef.current = binOutcome.nextState;
              setBinEntry(binOutcome.nextState);
            }
            if (binOutcome.commitAreaId !== undefined) {
              callbacks.handleSetArea(binOutcome.commitAreaId);
            }
            return;
          }

          const area = hotkeyCodeToArea[code];
          if (area) {
            event.preventDefault();
            callbacks.handleSetArea(area);
          }
          return;
        }

        // Record mode: hold the bin-entry key to record an area event with a
        // real duration (like holding a railcar key). The bin number is typed
        // afterwards, on the paused frame, by handleKeyUp -> bin entry.
        if (code === BIN_ENTRY_TRIGGER_CODE && binEntryAvailableRef.current) {
          event.preventDefault();
          if (
            !event.repeat &&
            !altHotkeyRecordingRef.current &&
            !recordingEventIdRef.current
          ) {
            const eventType = getNextEventType();
            altHotkeyRecordingRef.current = { code, area: null, eventType };
            if (binEntryRef.current.active) {
              binEntryRef.current = INACTIVE_BIN_ENTRY;
              setBinEntry(INACTIVE_BIN_ENTRY);
            }
            callbacks.handleStartRecording(eventType, null);
          }
          return;
        }

        // Enter finishes naming the bin: close entry and resume playback.
        if (binEntryRef.current.active && isEnterKey(code)) {
          event.preventDefault();
          binEntryRef.current = INACTIVE_BIN_ENTRY;
          setBinEntry(INACTIVE_BIN_ENTRY);
          playRef.current();
          return;
        }

        // After releasing the bin key the video is paused and bin entry is
        // armed: digits assign the bin to the just-recorded (still-selected)
        // event, refining as further digits are typed (1 -> 13).
        if (binEntryRef.current.active && isBinNumberKey(code)) {
          const binOutcome = reduceBinEntryKey(
            binEntryRef.current,
            code,
            binAreasRef.current,
            binEntryAvailableRef.current,
            true
          );
          if (binOutcome.consumed) {
            event.preventDefault();
            if (binOutcome.nextState !== binEntryRef.current) {
              binEntryRef.current = binOutcome.nextState;
              setBinEntry(binOutcome.nextState);
            }
            if (binOutcome.commitAreaId !== undefined) {
              callbacks.handleSetArea(binOutcome.commitAreaId);
            }
            return;
          }
        }

        // Any other key ends bin-number entry and falls through to the railcar
        // area keys below.
        if (binEntryRef.current.active) {
          binEntryRef.current = INACTIVE_BIN_ENTRY;
          setBinEntry(INACTIVE_BIN_ENTRY);
        }

        const area = hotkeyCodeToArea[code];
        if (area) {
          event.preventDefault();
        }
        if (
          area &&
          !event.repeat &&
          !altHotkeyRecordingRef.current &&
          !recordingEventIdRef.current
        ) {
          const eventType = getNextEventType();
          altHotkeyRecordingRef.current = { code, area, eventType };
          callbacks.handleStartRecording(eventType, area);
        }
        return;
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      const recording = altHotkeyRecordingRef.current;
      if (event.code === recording?.code) {
        event.preventDefault();
        altHotkeyRecordingRef.current = null;
        callbacks.handleStopRecording();
        // Releasing the bin-entry key pauses playback and arms bin-number
        // entry, so the labeller names the bin on the now-frozen frame.
        if (recording.code === BIN_ENTRY_TRIGGER_CODE) {
          pauseRef.current();
          binEntryRef.current = { active: true, buffer: "" };
          setBinEntry({ active: true, buffer: "" });
        }
        return;
      }
    };

    const handleBlur = () => {
      if (altHotkeyRecordingRef.current) {
        altHotkeyRecordingRef.current = null;
        callbacks.handleStopRecording();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, [hotkeyCodeToArea, callbacks, getNextEventType]);

  // Leaving area mode clears any half-typed bin number.
  useEffect(() => {
    if (isAreaModeActive) {
      return;
    }
    binEntryRef.current = INACTIVE_BIN_ENTRY;
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- clear transient bin-number buffer when area mode exits
    setBinEntry(INACTIVE_BIN_ENTRY);
  }, [isAreaModeActive]);

  return {
    isAreaModeActive,
    isShiftHeld,
    binEntryActive: isAreaModeActive && binEntry.active,
    binBuffer: binEntry.buffer,
    binEntryAvailable,
  };
};
